<template>
    <div>
        <v-btn size="small" @click="dialog = true"> {{ $t('auto_parsing_flight') }} </v-btn>
        <v-dialog v-model="dialog" width="auto" :disabled="loading">
            <v-card width="600" :title="$t('flight_route')">
                <v-card-text>
                    <v-textarea v-model="importString" rows="7" variant="outlined" />
                    <v-expansion-panels>
                        <v-expansion-panel :title="$t('view_sample')">
                            <v-expansion-panel-text>
                                <span class="text-caption" style="white-space: pre">
                                    {{ sample }}
                                </span>
                            </v-expansion-panel-text>
                        </v-expansion-panel>
                    </v-expansion-panels>
                </v-card-text>
                <v-card-actions>
                    <v-btn color="primary" :loading="loading" @click="parseFlightSegments(importString)">
                        {{ $t('import') }}
                    </v-btn>
                    <v-btn variant="text" @click="dialog = false">
                        {{ $t('close') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script lang="ts">
    import {Component, Emit, Vue} from 'vue-facing-decorator'
    import {clone} from '~/utils/helpers'

    @Component
    export class FlightRouteImportDialog extends Vue {
        importString = ''
        sample = `SK1758 12OCT GDN - CPH 06:10 07:10
QR162 12OCT CPH - DOH 09:20 16:20
QR914 12OCT DOH - ADL 20:20 16:30+1

QR915 19OCT ADL - DOH 21:40 03:55+1
QR6122 20OCT DOH - HEL 08:10 15:15
QR6181 20OCT HEL - GDN 18:00 19:05`
        dialog = false
        loading = false

        @Emit()
        changeStartDate(date) {
            return date
        }

        @Emit()
        changeEndDate(date) {
            return date
        }

        @Emit()
        changeSegments(segments) {
            return segments
        }

        async parseFlightSegments(input) {
            try {
                this.loading = true
                this.changeSegments([])
                await this.$nextTick

                const parseDate = (dateStr, timeStr) => {
                    const months = {
                        JAN: 0,
                        FEB: 1,
                        MAR: 2,
                        APR: 3,
                        MAY: 4,
                        JUN: 5,
                        JUL: 6,
                        AUG: 7,
                        SEP: 8,
                        OCT: 9,
                        NOV: 10,
                        DEC: 11,
                    }
                    const day = parseInt(dateStr.slice(0, 2), 10)
                    const month = months[dateStr.slice(2, 5).toUpperCase()]
                    const year = new Date().getFullYear() // Assuming the current year
                    const [hours, minutes] = timeStr.split(':').map(Number)

                    if (isNaN(day) || isNaN(month) || isNaN(hours) || isNaN(minutes)) {
                        return NaN
                    }

                    return new Date(Date.UTC(year, month, day, hours, minutes))
                }

                const segments = input
                    .split('\n')
                    .map(line => line.trim())
                    .filter(line => line)
                const details = []
                let minDate = null
                let maxDate = null

                segments.forEach(segment => {
                    const parts = segment.split(' ')
                    if (parts.length < 6) {
                        this.$toast.error(this.$t('error_message.invalid_data'))
                        console.error('Invalid segment format:', segment)
                        this.loading = false
                        return
                    }

                    const flightNumber = parts[0]
                    const departureDate = parts[1]
                    const departureAirport = parts[2]
                    const arrivalAirport = parts[4]
                    const departureTime = parts[5]
                    const arrivalTimeWithOffset = parts[6]
                    const arrivalTime = arrivalTimeWithOffset.split('+')[0]
                    const offset = arrivalTimeWithOffset.includes('+')
                        ? parseInt(arrivalTimeWithOffset.split('+')[1], 10)
                        : 0

                    const departureDateObj = new Date(parseDate(departureDate, departureTime))

                    if (isNaN(departureDateObj)) {
                        this.$toast.error(this.$t('error_message.invalid_data'))
                        console.error('Invalid departure date:', departureDate, departureTime)
                        this.loading = false
                        return
                    }

                    const arrivalDateObj = this.$dateFns.addDays(departureDateObj, offset)

                    const arrivalDateString = arrivalDateObj.toISOString().split('T')[0]
                    const departureDateString = departureDateObj.toISOString().split('T')[0]

                    details.push({
                        brandedFare: {name: ''},
                        departureAirportCode: departureAirport,
                        flightNumber: flightNumber,
                        departureDate: `${departureDateString}T${departureTime}`,
                        arrivalDate: `${arrivalDateString}T${arrivalTime}`,
                        arrivalAirportCode: arrivalAirport,
                    })

                    if (!minDate || departureDateObj < new Date(minDate)) {
                        minDate = departureDateObj.toISOString().split('T')[0]
                    }
                    if (!maxDate || arrivalDateObj > new Date(maxDate)) {
                        maxDate = arrivalDateObj.toISOString().split('T')[0]
                    }
                })

                this.changeSegments(clone(details))
                if (minDate) {
                    this.changeStartDate(minDate)
                }
                if (maxDate) {
                    this.changeEndDate(maxDate)
                }

                this.loading = false
                this.dialog = false

                return {
                    details,
                    minDate,
                    maxDate,
                }
            } catch {
                this.loading = false
                this.$toast.error(this.$t('error_message.invalid_data'))
            }
        }
    }

    export default FlightRouteImportDialog
</script>
